import { Icon } from '../../../../components/icon';
import { MaterialIcon } from '../../../../components/icon/icons';
import type { MenuComponent } from '../../../../components/menu/menu';
import { MenuItemComponent } from '../../../../components/menu/menu.item';
import { CSS as MenuCSS } from '../../../../components/menu/menu.style';
import { addStyles, DefaultTheme } from '../../../../components/theme';
import { T, Translate } from '../../../../i18n';
import type { MithrilVNode } from '../../../../mithril';
import { m } from '../../../../mithril';

export const SubformEditorDropdownCSS = {
	container: 'c-SubformEditorDropdown',
	titleContainer: 'c-SubformEditorDropdown__titleContainer',
	title: 'c-SubformEditorDropdown__title',
	menuContainer: 'c-SubformEditorDropdown__menuContainer',
	buttonRow: 'c-SubformEditorDropdown__buttonRow',
	button: 'c-SubformEditorDropdown__button',
	error: 'c-SubformEditorDropdown__error',
	iconLabel: 'c-SubformEditorDropdown__iconLabel',
	iconWrapper: 'c-SubformEditorDropdown__iconWrapper',
};

addStyles({
	[SubformEditorDropdownCSS.container]: {
		width: '100%',
		display: 'flex',
		'flex-direction': 'column',
		'& .mdl-button': {
			height: 'fit-content',
			width: 'fit-content',
		},
	},
	[SubformEditorDropdownCSS.title]: {
		color: DefaultTheme.font.color.supporting,
	},
	[SubformEditorDropdownCSS.menuContainer]: {
		display: 'flex',
		'flex-direction': 'column',
		position: 'relative',
		// Override the default menu positioning (top/right) so it drops below the trigger button and scrolls with it
		[`& .${MenuCSS.menu}`]: {
			top: '100%',
			right: 'auto',
			left: 0,
		},
		[`& .${MenuCSS.menu}.${MenuCSS.opensUpward}`]: {
			top: 'auto',
			bottom: '100%',
		},
	},
	[SubformEditorDropdownCSS.buttonRow]: {
		display: 'flex',
		'align-items': 'center',
		'flex-wrap': 'nowrap',
		gap: '8px',
	},
	[SubformEditorDropdownCSS.button]: {
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
		'white-space': 'nowrap',
	},
	[SubformEditorDropdownCSS.titleContainer]: {
		display: 'flex',
		'flex-direction': 'row',
		'align-items': 'center',
		gap: '8px',
		'margin-bottom': DefaultTheme.space(1),
		'font-size': '12px',
		'font-weight': 'bold',
	},
	[SubformEditorDropdownCSS.error]: {
		color: '#d32f2f',
	},
	[SubformEditorDropdownCSS.iconLabel]: {
		'border-radius': '50%',
		border: `1px solid ${DefaultTheme.background.grey}`,
		'background-color': DefaultTheme.background.lightGrey,
		'font-weight': 'bold',
		'font-size': '12px',
		height: '16px',
		width: '16px',
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		'font-style': 'normal',
	},
	[SubformEditorDropdownCSS.iconWrapper]: {
		margin: '0 8px',
	},
});

interface NoneState {
	isSelected: boolean;
	onSelect: () => void;
	view: MithrilVNode | null;
	position: 'top' | 'bottom';
}

const menuNoneState = new WeakMap<MenuComponent, NoneState>();

export const SubformEditorDropdown = {
	view(opts: {
		title: string;
		selectedText: string | MithrilVNode;
		menu: MenuComponent;
		error?: string | null;
		actionBtn?: { action: () => void; btnText: string; accent?: boolean };
		icon?: MithrilVNode;
		allowNone?: boolean;
		noneSelected?: boolean;
		onNoneSelect?: () => void;
		/** Overrides the default "None" menu item's content (icon + label). */
		noneItemView?: MithrilVNode;
		/** Where the "None" item appears in the list. Defaults to 'top'. */
		nonePosition?: 'top' | 'bottom';
	}): MithrilVNode {
		if (opts.allowNone && opts.onNoneSelect) {
			if (!menuNoneState.has(opts.menu)) {
				const state: NoneState = {
					isSelected: false,
					onSelect: opts.onNoneSelect,
					view: opts.noneItemView ?? null,
					position: opts.nonePosition ?? 'top',
				};
				menuNoneState.set(opts.menu, state);
				const originalItemsFunc = opts.menu.itemsFunc;
				opts.menu.itemsFunc = () => {
					if (state.isSelected) return originalItemsFunc();
					const noneItem = { view: state.view ?? MenuItemComponent.view([Translate(T.NONE)]), action: state.onSelect };
					return state.position === 'bottom' ? [...originalItemsFunc(), noneItem] : [noneItem, ...originalItemsFunc()];
				};
			}
			const state = menuNoneState.get(opts.menu)!;
			state.isSelected = !!opts.noneSelected;
			state.onSelect = opts.onNoneSelect;
			state.view = opts.noneItemView ?? null;
			state.position = opts.nonePosition ?? 'top';
		}
		return m('div', { className: SubformEditorDropdownCSS.container }, [
			m('div', { className: SubformEditorDropdownCSS.titleContainer }, [
				m('div', { className: SubformEditorDropdownCSS.title }, opts.title),
				opts.error ? m('div', { className: SubformEditorDropdownCSS.error }, opts.error) : null,
			]),
			m('div', { className: SubformEditorDropdownCSS.menuContainer }, [
				m('div', { className: SubformEditorDropdownCSS.buttonRow }, [
					m(
						'button',
						{
							type: 'button',
							title: opts.title,
							className: `mdl-button ${SubformEditorDropdownCSS.button}`,
							onclick: () => (opts.menu.isOpen ? opts.menu.hide() : opts.menu.show()),
						},
						[opts.icon ? opts.icon : null, opts.selectedText, Icon.view(MaterialIcon.KEY_DOWN)]
					),
					opts.actionBtn
						? m(
								'button',
								{
									type: 'button',
									title: opts.title,
									className: `mdl-button ${
										opts.actionBtn.accent ? 'mdl-button--raised mdl-button--accent ' : ''
									}${SubformEditorDropdownCSS.button}`,
									onclick: () => opts.actionBtn?.action(),
								},
								opts.actionBtn.btnText
							)
						: null,
				]),
				opts.menu.view(),
			]),
		]);
	},
};
